import { API_ENDPOINT } from "./api";
import { authenticatedFetch } from "./auth";

/**
 * Handles the calculation of the active days of the current user
 * @param {number} historyDays - look at last n days
 * @param {string} language - practice language to scope the stats to (e.g. "de-DE")
 *
 * @returns {Promise} - API response with a list of length n with the value false when the user was inactive and
 *                      true when the user was active. The oldest day (today - n) is at the beginning of the list.
 */
export const getActivityList = async (historyDays, language) => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await authenticatedFetch(`${API_ENDPOINT}/dashboard/daysActive/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                historyDays,
                timezone,
                language,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to fetch activity history");
        }

        return await response.json();
    } catch (error) {
        // Re-throw error for handling by caller
        throw error;
    }
};

/**
 * Fetches today's score, sentence count, and estimated practice time.
 *
 * @param {string} language - practice language to scope the stats to (e.g. "de-DE")
 *
 * @returns {Promise<{score: number, sentences_today: number, sentences_goal: number, practice_minutes: number}>}
 */
export const getTodayStats = async (language) => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await authenticatedFetch(`${API_ENDPOINT}/dashboard/todayStats/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ timezone, language }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to fetch today's stats");
        }

        return await response.json();
    } catch (error) {
        throw error;
    }
};

/**
 * Handles retrieving the current streak
 *
 * @param {string} language - practice language to scope the streak to (e.g. "de-DE")
 *
 * @returns {Promise} - API response with a number of the current streak. Greater or equal to 1
 */
export const getStreak = async (language) => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await authenticatedFetch(`${API_ENDPOINT}/dashboard/currentStreak/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                timezone,
                language,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to fetch activity history");
        }

        return await response.json();
    } catch (error) {
        // Re-throw error for handling by caller
        throw error;
    }
};
